/**
 * probe_suites — the metric suites the in-page probe runner executes.
 *
 * Everything here is DIAGNOSTIC-ONLY code behind the `?probe=` URL param (see
 * AtlasProbeRunnerModule). Each suite writes human-readable lines/tables into
 * the overlay (agent/extension-readable DOM) and a JSON node into the report.
 * Suites reuse the always-on debug hooks (__drawCensus, __phys*, __unstick)
 * rather than reaching into module internals.
 */
import { atlas } from '../state.js';
import { TIER_ORDER, activeTier, activePreset, type Tier } from '../lib/quality_tiers.js';
import { settlementSites } from '@shared/settlements.js';
import { terrainHeight } from '@shared/terrain.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { capsIn } from '../lib/world_caps.js';
import {
  labBaseSubs, labScopedSubs, pickCast, runLab, renderLabReportMd, labRunToJson,
  tbl as labTbl,
} from '../lib/versona_lab.js';
import { computeCensus, censusExtraSubs, renderCensusMd, censusToJson } from '../lib/versona_census.js';

export interface FrameReport {
  n: number; medMs: number; p95Ms: number; p99Ms: number; maxMs: number; fps: number;
  hitch50: number; hitch100: number;
  longCount: number; longTotalMs: number; longMaxMs: number;
  gpuMedMs: number | null; gpuMaxMs: number | null;
}

export interface ProbeCtx {
  log(line: string): void;
  section(title: string): void;
  check(label: string, ok: boolean, detail?: string): boolean;
  table(title: string, rows: Record<string, Record<string, unknown>>): void;
  sleep(ms: number): Promise<void>;
  teleport(x: number, z: number): Promise<boolean>;
  measureFrames(ms: number): Promise<FrameReport>;
  holdIntent(dir: [number, number] | null): void;
  fast: boolean;
  report: Record<string, unknown>;
  bootMarks: Record<string, number>;
  rendererNative(): unknown;
}

type G = {
  __drawCensus?: () => { total: number; rows: string[] };
  __phys?: () => { ready: boolean; statics: number; terrain: number };
  __physSelf?: () => { x: number; z: number; feetY: number; overlap: boolean; ceiling: boolean; terrainOverlap: boolean } | null;
  __physSeat?: (x: number, z: number) => boolean;
  __physAddBox?: (x: number, z: number, yOff?: number, half?: number) => boolean;
  __physAddTerrainPatch?: (x: number, z: number, lift?: number) => boolean;
  __unstickNow?: (esc?: boolean, anyClass?: boolean) => { x: number; z: number } | null;
  __unstick?: { scans: number; trapped: number; relocations: number; depen: number; wedged: number };
};
const g = (): G => globalThis as unknown as G;

/** Scene composition without renderer.info.render (unusable under the post path). */
function sceneComp(scene: { traverseVisible(cb: (o: unknown) => void): void }): { drawables: number; instances: number; mtris: number } {
  let drawables = 0, instances = 0, tris = 0;
  scene.traverseVisible((obj) => {
    const m = obj as { isMesh?: boolean; isInstancedMesh?: boolean; isBatchedMesh?: boolean; count?: number; geometry?: { index?: { count: number } | null; attributes?: { position?: { count: number } } } };
    if (!(m.isMesh || m.isInstancedMesh || m.isBatchedMesh)) return;
    drawables++;
    const n = m.isInstancedMesh ? (m.count ?? 0) : 1;
    instances += n;
    const geo = m.geometry;
    if (geo) tris += Math.round(((geo.index ? geo.index.count : geo.attributes?.position?.count ?? 0) / 3) * n);
  });
  return { drawables, instances, mtris: +(tris / 1e6).toFixed(2) };
}

function heapMB(): number | null {
  const p = performance as unknown as { memory?: { usedJSHeapSize: number } };
  return p.memory ? +(p.memory.usedJSHeapSize / 1048576).toFixed(0) : null;
}

function rendererMem(native: unknown): { geometries: number; textures: number } | null {
  const info = (native as { info?: { memory?: { geometries: number; textures: number } } }).info;
  return info?.memory ? { geometries: info.memory.geometries, textures: info.memory.textures } : null;
}

/**
 * Wait until the scene stops changing (drawables move < 2% across two
 * consecutive 3 s polls, 60 s cap) — tier switches trigger pipeline rebuilds
 * + foliage re-scatters whose work otherwise lands inside measurement windows
 * (the real-GPU tiers run: low's window was n=32 frames swallowed by a 6.9 s
 * re-scatter stall).
 */
async function stabilizeScene(ctx: ProbeCtx): Promise<void> {
  let prev = -1, stableTwice = 0;
  for (let i = 0; i < 20 && stableTwice < 2; i++) {
    await ctx.sleep(3_000);
    const d = sceneComp(scene()).drawables;
    if (prev > 0 && Math.abs(d - prev) <= Math.max(2, prev * 0.02)) stableTwice++; else stableTwice = 0;
    prev = d;
  }
  // Quiet-window precondition: drawable counts can't see PENDING pipeline
  // compiles / shadow-map builds — 6.8 s stalls still landed inside windows
  // after content stabilized. Require one 3 s sample with no frame > 500 ms
  // (up to 3 tries) before declaring the site measurable.
  for (let tries = 0; tries < 3; tries++) {
    const probe = await ctx.measureFrames(3_000);
    if (probe.maxMs < 500) return;
  }
}

/**
 * Mean canvas luma 0-255 (32×32 downsample via createImageBitmap — works on a
 * WebGPU canvas without preserveDrawingBuffer, unlike getImageData). The
 * black-frame detector: a tiers sweep once measured four tiers against a black
 * viewport (the walk phases had marched the avatar inside an unlit tower) and
 * only the reading agent noticed. null = sampling unavailable.
 */
async function canvasLuma(): Promise<number | null> {
  try {
    const canvas = (atlas as unknown as { app: { renderer: { native: { domElement?: HTMLCanvasElement } } } }).app.renderer.native.domElement;
    if (!canvas) return null;
    // Sample inside a rAF callback: WebGPU ignores preserveDrawingBuffer and
    // clears the drawing buffer on composite, so a read from a plain task sees
    // black (gpuweb#1781 / #2743) — the same-frame task is the only safe spot.
    const bmp = await new Promise<ImageBitmap>((resolve, reject) => {
      requestAnimationFrame(() => { createImageBitmap(canvas, { resizeWidth: 32, resizeHeight: 32 }).then(resolve, reject); });
    });
    const c2 = document.createElement('canvas');
    c2.width = 32; c2.height = 32;
    const g2 = c2.getContext('2d');
    if (!g2) return null;
    g2.drawImage(bmp, 0, 0);
    bmp.close();
    const d = g2.getImageData(0, 0, 32, 32).data;
    let sum = 0;
    for (let i = 0; i < d.length; i += 4) sum += 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
    return +(sum / (d.length / 4)).toFixed(1);
  } catch { return null; }
}

/**
 * The perf-grade site profile both the perf tour and the tier sweeps share:
 * an IDLE window and a WALKING window (rotating intent every 4 s so streaming
 * and pipeline hitches surface), plus scene composition, heap, and the top
 * census rows. Every FrameReport already carries hitch counts, long tasks,
 * and GPU ms.
 */
async function profileHere(ctx: ProbeCtx, windowMs: number): Promise<{
  idle: FrameReport; walk: FrameReport;
  idleLuma: number | null; walkLuma: number | null;
  sceneComp: { drawables: number; instances: number; mtris: number };
  heapMB: number | null; censusTop: string[];
}> {
  const idle = await ctx.measureFrames(windowMs);
  const idleLuma = await canvasLuma();
  const dirs: [number, number][] = [[0, -1], [1, 0], [0, 1], [-1, 0]];
  let i = 0;
  const rot = setInterval(() => ctx.holdIntent(dirs[++i % 4]), 4000);
  ctx.holdIntent(dirs[0]);
  const walk = await ctx.measureFrames(windowMs);
  clearInterval(rot);
  ctx.holdIntent(null);
  const walkLuma = await canvasLuma();
  if (idleLuma !== null && idleLuma < 3) ctx.log(`  ⚠ BLACK/NEAR-BLACK viewport during idle window (luma ${idleLuma}/255) — vantage/rendering problem, OR (exact 0 on WebGPU) an unsampleable cleared back buffer: cross-check what is actually on screen`);
  return { idle, walk, idleLuma, walkLuma, sceneComp: sceneComp(scene()), heapMB: heapMB(), censusTop: g().__drawCensus?.()?.rows.slice(0, 6) ?? [] };
}

function frameRow(f: FrameReport): Record<string, unknown> {
  return { 'med ms': f.medMs, p95: f.p95Ms, p99: f.p99Ms, max: f.maxMs, fps: f.fps, '>50ms': f.hitch50, '>100ms': f.hitch100, long: f.longCount, 'gpu med': f.gpuMedMs ?? 'n/a' };
}

function commonsSeed(): string {
  const conn = atlas.conn;
  if (!conn) return '';
  for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
  return '';
}

function scene(): { traverseVisible(cb: (o: unknown) => void): void } {
  return (atlas as unknown as { app: { world: { scene: { traverseVisible(cb: (o: unknown) => void): void } } } }).app.world.scene;
}

// Re-verified against the LIVE seed 2026-07-28 (site-scan): the old coast/
// mountain coords predate the waterline drop to −166.6 m (F13) — "coast-surf"
// was dry sand and "mountain-snowline" sat below the 320 m snow band.
const SITES = [
  { name: 'spawn-city', x: 211, z: 75 },
  { name: 'coast-surf', x: -157239, z: -15487 },      // h −164.1, water ≤240 m away
  { name: 'mountain-snowline', x: -19411, z: -72444 }, // h 330.2 (snow band 320–380)
  { name: 'lake-plains', x: 1041, z: 290 },
];

// ── boot ─────────────────────────────────────────────────────────────────────
export async function suiteBoot(ctx: ProbeCtx): Promise<void> {
  ctx.section('boot — startup timeline');
  const m = ctx.bootMarks;
  const rel = (k: string): string => (m[k] !== undefined ? `${((m[k] - m.init) / 1000).toFixed(1)}s` : 'n/a');
  ctx.table('boot timeline (from module init)', {
    marks: { 'account-ready': rel('accountReady'), 'world-entered': rel('worldEntered'), 'gate-drop': rel('gateDrop'), 'suites-start': `${((performance.now() - m.init) / 1000).toFixed(1)}s` },
  });
  ctx.report.marks = Object.fromEntries(Object.entries(m).map(([k, v]) => [k, +((v - m.init) / 1000).toFixed(1)]));
  ctx.check('loading gate dropped', atlas.loadingGate === false);
  ctx.check('physics ready', g().__phys?.()?.ready === true);
  ctx.report.heapMB = heapMB();
}

// ── render state ─────────────────────────────────────────────────────────────
export async function suiteRender(ctx: ProbeCtx): Promise<void> {
  ctx.section('render — backend + pipeline state');
  const native = ctx.rendererNative() as { backend?: { constructor?: { name?: string } }; getPixelRatio?: () => number };
  const backend = native.backend?.constructor?.name ?? '?';
  const preset = activePreset();
  const post = Object.keys(preset.post).join(',') || 'none';
  let lights = 0;
  scene().traverseVisible((o) => { if ((o as { isLight?: boolean }).isLight) lights++; });
  const comp = sceneComp(scene());
  ctx.table('render state', {
    state: {
      backend, tier: activeTier(), pixelRatio: native.getPixelRatio?.() ?? '?', dpr: window.devicePixelRatio,
      post, clouds: preset.cloudsEnabled, shadows: preset.shadowsEnabled, lights,
    },
  });
  ctx.check('WebGPU backend (production path)', backend === 'WebGPUBackend', backend);
  ctx.table('scene composition', { scene: comp as unknown as Record<string, unknown> });
  const census = g().__drawCensus?.();
  if (census) { for (const r of census.rows.slice(0, 8)) ctx.log(`  ${r}`); ctx.report.censusTop = census.rows.slice(0, 10); }
  Object.assign(ctx.report, { backend, tier: activeTier(), lights, sceneComp: comp });
}

// ── perf tour ────────────────────────────────────────────────────────────────
export async function suitePerf(ctx: ProbeCtx): Promise<void> {
  ctx.section('perf — 4-site tour (idle + walk)');
  // Context header (the first real-GPU run came back without tier/backend and
  // with unexplained gpu=n/a — record the WHY up front).
  const native = ctx.rendererNative() as { backend?: { constructor?: { name?: string }; hasFeature?: (f: string) => boolean } };
  const backend = native.backend?.constructor?.name ?? '?';
  const tsFeature = native.backend?.hasFeature?.('timestamp-query') === true || native.backend?.hasFeature?.('EXT_disjoint_timer_query_webgl2') === true;
  const gpuTimingWired = ((): boolean => { try { return !!(ctx.rendererNative(), (atlas as unknown as { app: { renderer: { gpuTiming?: unknown } } }).app.renderer.gpuTiming); } catch { return false; } })();
  ctx.log(`context: backend=${backend} tier=${activeTier()} pixelRatio=${(native as { getPixelRatio?: () => number }).getPixelRatio?.() ?? '?'} timestampFeature=${tsFeature} gpuTimingWired=${gpuTimingWired}`);
  if (!gpuTimingWired) ctx.log('  gpu columns will be n/a — open with ?perf=1 (trackTimestamp) and a timestamp-query-capable backend');
  Object.assign(ctx.report, { backend, tier: activeTier(), timestampFeature: tsFeature, gpuTimingWired });
  const settleMs = ctx.fast ? 10_000 : 30_000;
  const windowMs = ctx.fast ? 6_000 : 15_000;
  const sites: Record<string, unknown>[] = [];
  for (const s of SITES) {
    ctx.log(`→ ${s.name} (${s.x}, ${s.z}) — settling ${settleMs / 1000}s`);
    if (!await ctx.teleport(s.x, s.z)) { ctx.check(`${s.name}: teleport`, false); continue; }
    await ctx.sleep(settleMs);
    const p = await profileHere(ctx, windowMs);
    ctx.table(`${s.name}`, { idle: frameRow(p.idle), walk: frameRow(p.walk) });
    ctx.log(`  draw ${p.sceneComp.drawables} obj / ${p.sceneComp.instances} inst / ${p.sceneComp.mtris}M tri · heap ${p.heapMB} MB`);
    sites.push({ site: s.name, ...p });
    // 120 ms: two skipped 60 Hz vsyncs of margin — the first real-GPU run
    // "failed" on p99=100 against a <100 threshold, a boundary artifact.
    ctx.check(`${s.name}: no severe hitches while idle (p99 < 120 ms)`, p.idle.p99Ms < 120, `p99=${p.idle.p99Ms}`);
    // Multi-second single frames are first-arrival costs (cold-cache GLB/
    // texture/pipeline work) — informational, not a check: a warm-cache rerun
    // that still shows them IS a finding.
    if (p.idle.maxMs > 1000 || p.walk.maxMs > 1000) ctx.log(`  ⚠ multi-second stall frames: idle max ${p.idle.maxMs} ms / walk max ${p.walk.maxMs} ms (cold-cache first-arrival class — rerun warm to confirm)`);
  }
  ctx.report.sites = sites;
}

// ── tier sweep ───────────────────────────────────────────────────────────────
export async function suiteTiers(ctx: ProbeCtx): Promise<void> {
  ctx.section('tiers — live sweep at an open vantage (lake-plains)');
  // A CONTROLLED open site, not "wherever the player stands": the sweep once
  // ran at the arrival pad, the walk phases marched the avatar into the city
  // core, and four tiers were measured from inside an unlit tower interior
  // (pitch black once shadows enabled). Lake-plains has no interiors to enter.
  if (!await ctx.teleport(1041, 290)) { ctx.check('tiers: vantage teleport', false); return; }
  await stabilizeScene(ctx);
  const saved = localStorage.getItem('atlas:graphics');
  const original = activeTier();
  const post = (atlas as unknown as { app: { getModule(n: string): unknown } }).app.getModule('Post') as { setTier?: (t: Tier) => void } | undefined;
  if (!post?.setTier) { ctx.check('Post module reachable', false); return; }
  const windowMs = ctx.fast ? 4_000 : 8_000;
  const rows: Record<string, Record<string, unknown>> = {};
  const out: Record<string, unknown>[] = [];
  for (const t of TIER_ORDER) {
    post.setTier(t);
    await ctx.sleep(2_000);
    await stabilizeScene(ctx); // switch work (pipeline rebuild, re-scatter) must NOT land in the window
    const p = await profileHere(ctx, windowMs);
    const ratio = (ctx.rendererNative() as { getPixelRatio?: () => number }).getPixelRatio?.() ?? '?';
    if (p.idle.n < 60) ctx.log(`  ⚠ ${t}: only ${p.idle.n} idle frames sampled — stall-dominated window, low confidence`);
    rows[`${t} idle`] = { ...frameRow(p.idle), ratio, luma: p.idleLuma ?? '?', inst: p.sceneComp.instances, mtris: p.sceneComp.mtris, heap: p.heapMB ?? '?' };
    rows[`${t} walk`] = frameRow(p.walk);
    out.push({ tier: t, ratio, ...p });
    ctx.log(`  ${t}: idle ${p.idle.medMs} ms (${p.idle.fps} fps) · walk ${p.walk.medMs} ms (${p.walk.fps} fps) · ratio ${ratio} · ${p.sceneComp.mtris}M tri · heap ${p.heapMB} MB`);
    for (const row of p.censusTop.slice(0, 4)) ctx.log(`    ${row}`);
  }
  ctx.table('per-tier (same site, live switch, idle + walk)', rows);
  // Monotonicity on the real pipeline: potato must not out-triangle ultra.
  const tri = (i: number): number => (out[i].sceneComp as { mtris: number }).mtris;
  ctx.check('potato renders ≤ ultra triangles (foliage knob live)', tri(0) <= tri(TIER_ORDER.length - 1) * 1.15, `${tri(0)}M vs ${tri(TIER_ORDER.length - 1)}M`);
  ctx.report.tiers = out;
  // Restore: setTier FIRST (it PERSISTS a clean preset — calling it after the
  // verbatim restore clobbered the player's saved/custom settings; the first
  // real-GPU sweep left the renderer parked wrong), THEN write the saved
  // string back verbatim so a reload returns the exact pre-sweep settings.
  post.setTier(original);
  if (saved !== null) localStorage.setItem('atlas:graphics', saved); else localStorage.removeItem('atlas:graphics');
  ctx.log(`restored tier '${original}' live; persisted settings restored verbatim (custom tweaks return on reload)`);
}

// ── tier × site matrix (the FULL per-tier performance picture) ───────────────
export async function suiteTiersTour(ctx: ProbeCtx): Promise<void> {
  ctx.section('tierstour — full tier × site performance matrix');
  ctx.log(`runs the 4-site perf tour at EVERY tier — ${ctx.fast ? '~15–25' : '~35–60'} min; &probeFast=1 recommended`);
  const saved = localStorage.getItem('atlas:graphics');
  const original = activeTier();
  const post = (atlas as unknown as { app: { getModule(n: string): unknown } }).app.getModule('Post') as { setTier?: (t: Tier) => void } | undefined;
  if (!post?.setTier) { ctx.check('Post module reachable', false); return; }
  const windowMs = ctx.fast ? 5_000 : 10_000;
  const matrix: Record<string, unknown>[] = [];
  for (const t of TIER_ORDER) {
    post.setTier(t);
    await ctx.sleep(2_000);
    const rows: Record<string, Record<string, unknown>> = {};
    for (const s of SITES) {
      if (!await ctx.teleport(s.x, s.z)) { ctx.check(`${t}/${s.name}: teleport`, false); continue; }
      await stabilizeScene(ctx);
      const p = await profileHere(ctx, windowMs);
      rows[`${s.name} idle`] = { ...frameRow(p.idle), mtris: p.sceneComp.mtris };
      rows[`${s.name} walk`] = frameRow(p.walk);
      matrix.push({ tier: t, site: s.name, ...p });
      ctx.log(`  ${t} · ${s.name}: idle ${p.idle.medMs} ms (${p.idle.fps} fps) · walk ${p.walk.medMs} ms · ${p.sceneComp.mtris}M tri`);
    }
    ctx.table(`tier ${t}`, rows);
  }
  ctx.report.matrix = matrix;
  // Same restore order as suiteTiers: re-apply live FIRST (setTier persists a
  // clean preset), then put the saved settings back verbatim.
  post.setTier(original);
  if (saved !== null) localStorage.setItem('atlas:graphics', saved); else localStorage.removeItem('atlas:graphics');
  ctx.log(`restored tier '${original}' live; persisted settings restored verbatim`);
}

// ── movement / feel ──────────────────────────────────────────────────────────
export async function suiteMovement(ctx: ProbeCtx): Promise<void> {
  ctx.section('movement — displacement, divergence, rubber-band, jump');
  const conn = atlas.conn;
  if (!conn?.identity) { ctx.check('connection', false); return; }
  const segMs = ctx.fast ? 3_000 : 5_000;
  const dirs: { name: string; d: [number, number] }[] = [
    { name: 'north', d: [0, -1] }, { name: 'east', d: [1, 0] }, { name: 'south', d: [0, 1] }, { name: 'west', d: [-1, 0] },
  ];
  const rows: Record<string, Record<string, unknown>> = {};
  const out: Record<string, unknown>[] = [];
  for (const dir of dirs) {
    const seq0 = (conn.db.playerState.identity.find(conn.identity)?.inputSeq ?? 0);
    const start = { x: atlas.localX, z: atlas.localZ };
    const div: number[] = [];
    let snaps = 0;
    let prev = { x: atlas.localX, z: atlas.localZ };
    ctx.holdIntent(dir.d);
    const t0 = performance.now();
    while (performance.now() - t0 < segMs) {
      await ctx.sleep(100);
      const row = conn.db.playerState.identity.find(conn.identity);
      if (row) div.push(Math.hypot(atlas.localX - row.posX, atlas.localZ - row.posZ));
      const jump = Math.hypot(atlas.localX - prev.x, atlas.localZ - prev.z);
      if (jump > 2) snaps++; // >2 m per 100 ms while walking 6 m/s = a snap/rubber-band
      prev = { x: atlas.localX, z: atlas.localZ };
    }
    ctx.holdIntent(null);
    await ctx.sleep(400);
    const moved = Math.hypot(atlas.localX - start.x, atlas.localZ - start.z);
    const speed = +(moved / (segMs / 1000)).toFixed(2);
    const seq1 = (conn.db.playerState.identity.find(conn.identity)?.inputSeq ?? 0);
    const divMed = div.length ? +div.sort((a, b) => a - b)[Math.floor(div.length / 2)].toFixed(2) : 0;
    const divMax = div.length ? +Math.max(...div).toFixed(2) : 0;
    rows[dir.name] = { 'm/s': speed, 'div med m': divMed, 'div max m': divMax, snaps, 'acks': seq1 - seq0 };
    out.push({ dir: dir.name, speed, divMed, divMax, snaps, acks: seq1 - seq0 });
    ctx.check(`${dir.name}: moved (speed ${speed} m/s)`, speed > 1, 'blocked or input not registering');
    ctx.check(`${dir.name}: server tracked (acks > 0)`, seq1 > seq0);
  }
  ctx.table('movement segments (5 s each)', rows);
  // Jump: press → airborne → grounded again.
  const y0 = atlas.localY;
  atlas.intentJump = true;
  let rose = false;
  const tj = performance.now();
  while (performance.now() - tj < 2_000) { await ctx.sleep(50); if (atlas.localY > y0 + 0.35) { rose = true; break; } }
  await ctx.sleep(1_500);
  ctx.check('jump: airborne then landed', rose && Math.abs(atlas.localY - y0) < 1.5, `y ${y0.toFixed(2)}→${atlas.localY.toFixed(2)}`);
  ctx.report.segments = out;
  ctx.report.jump = { rose, y0: +y0.toFixed(2), yEnd: +atlas.localY.toFixed(2) };
}

// ── network health ───────────────────────────────────────────────────────────
export async function suiteNet(ctx: ProbeCtx): Promise<void> {
  ctx.section('net — RTT, row caches, replication rates');
  const conn = atlas.conn;
  if (!conn?.identity) { ctx.check('connection', false); return; }
  // Server-ack RTT: tiny debugTeleport write → row mirrored back.
  const rtts: number[] = [];
  for (let i = 0; i < (ctx.fast ? 4 : 8); i++) {
    const r0 = conn.db.playerState.identity.find(conn.identity);
    if (!r0) break;
    const nx = r0.posX + 0.02;
    const t0 = performance.now();
    await conn.reducers.debugTeleport({ posX: nx, posZ: r0.posZ });
    for (let k = 0; k < 200; k++) {
      const r = conn.db.playerState.identity.find(conn.identity);
      if (r && Math.abs(r.posX - nx) < 1e-3) break;
      await ctx.sleep(10);
    }
    rtts.push(performance.now() - t0);
    await ctx.sleep(150);
  }
  const rttMed = rtts.length ? +rtts.sort((a, b) => a - b)[Math.floor(rtts.length / 2)].toFixed(0) : -1;
  ctx.check('server-ack RTT sampled', rtts.length > 0);
  ctx.log(`RTT median ${rttMed} ms · max ${Math.max(0, ...rtts).toFixed(0)} ms (n=${rtts.length})`);
  // Row-cache census over a fixed table list (guarded — bindings vary).
  const db = conn.db as unknown as Record<string, { iter?: () => Iterable<unknown> }>;
  const tables = ['playerState', 'presence', 'versonaState', 'npcAgent', 'structure', 'worldObject', 'adminPlacement', 'chatMessage', 'world'];
  const cache: Record<string, number> = {};
  for (const t of tables) { try { cache[t] = db[t]?.iter ? [...db[t].iter!()].length : -1; } catch { cache[t] = -1; } }
  ctx.table('row caches', { rows: cache });
  // Replication rate meters (updates/s on the hot tables).
  let vsUpd = 0, psUpd = 0;
  try { (conn.db as unknown as { versonaState?: { onUpdate(cb: () => void): void } }).versonaState?.onUpdate(() => vsUpd++); } catch { /* absent */ }
  try { conn.db.playerState.onUpdate(() => psUpd++); } catch { /* absent */ }
  const meterMs = ctx.fast ? 5_000 : 10_000;
  await ctx.sleep(meterMs);
  const vsRate = +(vsUpd / (meterMs / 1000)).toFixed(1);
  const psRate = +(psUpd / (meterMs / 1000)).toFixed(1);
  ctx.log(`replication: versona_state ${vsRate}/s · player_state ${psRate}/s over ${meterMs / 1000}s`);
  ctx.check('no versona replication storm (< 80 rows/s mirrored)', vsRate < 80, `${vsRate}/s`);
  ctx.report.rtt = { medMs: rttMed, samples: rtts.map((v) => +v.toFixed(0)) };
  ctx.report.rowCaches = cache;
  ctx.report.replicationPerSec = { versonaState: vsRate, playerState: psRate };
  ctx.report.remotes = atlas.remotes.size;
}

// ── world integrity scenarios ────────────────────────────────────────────────
export async function suiteWorld(ctx: ProbeCtx): Promise<void> {
  ctx.section('world — per-scenario content integrity');
  const seed = commonsSeed();
  if (!seed) { ctx.check('commons seed', false); return; }
  const sea = commonsSeaLevel(seed);
  const sites = settlementSites(seed);
  const nearest = (tier: string): { x: number; z: number } | null => {
    let best: { x: number; z: number } | null = null, bd = Infinity;
    for (const s of sites) {
      if (s.tier !== tier) continue;
      const d = Math.hypot(s.centerX, s.centerZ);
      if (d < bd) { bd = d; best = { x: s.centerX, z: s.centerZ }; }
    }
    return best;
  };
  const town = nearest('town');
  const hamlet = nearest('hamlet');
  const cases: { name: string; x: number; z: number; expect: 'settlement' | 'coast' | 'wilds' }[] = [
    { name: 'hub-city', x: 211, z: 75, expect: 'settlement' },
    ...(town ? [{ name: 'nearest-town', x: town.x + 40, z: town.z, expect: 'settlement' as const }] : []),
    ...(hamlet ? [{ name: 'nearest-hamlet', x: hamlet.x + 30, z: hamlet.z, expect: 'settlement' as const }] : []),
    { name: 'coast', x: -135867, z: 6016, expect: 'coast' },
    { name: 'mountain', x: -127500, z: -189516, expect: 'wilds' },
    { name: 'lake-plains', x: 1041, z: 290, expect: 'wilds' },
    { name: 'deep-wilderness', x: 52000, z: -47000, expect: 'wilds' },
  ];
  const settleMs = ctx.fast ? 8_000 : 20_000;
  const out: Record<string, unknown>[] = [];
  for (const c of cases) {
    ctx.log(`→ ${c.name} (${Math.round(c.x)}, ${Math.round(c.z)})`);
    if (!await ctx.teleport(c.x, c.z)) { ctx.check(`${c.name}: teleport`, false); continue; }
    await ctx.sleep(settleMs);
    const phys = g().__phys?.();
    const self = g().__physSelf?.();
    const comp = sceneComp(scene());
    const h = terrainHeight(seed, c.x, c.z);
    const row: Record<string, unknown> = {
      terrainColliders: phys?.terrain ?? -1, structureColliders: phys?.statics ?? -1,
      drawables: comp.drawables, instances: comp.instances, mtris: comp.mtris,
      feetY: self ? +self.feetY.toFixed(1) : null, analyticH: +h.toFixed(1), aboveSea: h > sea,
    };
    ctx.check(`${c.name}: terrain colliders present`, (phys?.terrain ?? 0) > 0);
    ctx.check(`${c.name}: player position finite + not embedded`, !!self && Number.isFinite(self.feetY) && !self.overlap && !self.terrainOverlap);
    ctx.check(`${c.name}: content rendered (drawables > 15)`, comp.drawables > 15, `${comp.drawables}`);
    if (c.expect === 'settlement') ctx.check(`${c.name}: structure colliders streamed`, (phys?.statics ?? 0) > 0, `${phys?.statics}`);
    if (c.expect === 'settlement') row.versonaRows = ((): number => { try { return [...(atlas.conn!.db as unknown as { versonaState: { iter(): Iterable<unknown> } }).versonaState.iter()].length; } catch { return -1; } })();
    out.push({ case: c.name, ...row });
    ctx.table(c.name, { metrics: row });
  }
  ctx.report.cases = out;
}

// ── memory / leak cycle ──────────────────────────────────────────────────────
export async function suiteMemory(ctx: ProbeCtx): Promise<void> {
  ctx.section('memory — revisit cycle (leak signal)');
  // Snapshot only once the scene STOPS CHANGING — a fixed settle after a
  // failsafe'd gate measured a mid-stream world (first real-GPU run: "endless
  // water, no terrain" at snapshot time). Stable = drawable count moves < 2%
  // across two consecutive 3 s polls, capped at 60 s.
  const stabilize = (): Promise<void> => stabilizeScene(ctx);
  const snap = (label: string): Record<string, unknown> => {
    const rm = rendererMem(ctx.rendererNative());
    const s = { label, heapMB: heapMB(), geometries: rm?.geometries ?? -1, textures: rm?.textures ?? -1, drawables: sceneComp(scene()).drawables };
    ctx.log(`  ${label}: heap ${s.heapMB} MB · geo ${s.geometries} · tex ${s.textures} · draw ${s.drawables}`);
    return s;
  };
  const A = SITES[0], B = SITES[3];
  await ctx.teleport(A.x, A.z); await stabilize();
  const s1 = snap('A first visit');
  await ctx.teleport(B.x, B.z); await stabilize();
  const s2 = snap('B');
  await ctx.teleport(A.x, A.z); await stabilize();
  const s3 = snap('A revisit');
  const geoGrowth = (s3.geometries as number) - (s1.geometries as number);
  const texGrowth = (s3.textures as number) - (s1.textures as number);
  ctx.check('geometries stable on revisit (Δ ≤ 25%)', (s1.geometries as number) <= 0 || geoGrowth <= (s1.geometries as number) * 0.25, `Δ${geoGrowth}`);
  ctx.check('textures stable on revisit (Δ ≤ 25%)', (s1.textures as number) <= 0 || texGrowth <= (s1.textures as number) * 0.25, `Δ${texGrowth}`);
  // Heap gate (agent feedback: it was reported but never checked). Generous
  // ±30% — GC timing is nondeterministic; the plateau signal is what matters.
  const h1 = s1.heapMB as number | null, h3 = s3.heapMB as number | null;
  if (h1 && h3) ctx.check('heap stable on revisit (Δ ≤ 30%)', h3 <= h1 * 1.3, `${h1}→${h3} MB`);
  else ctx.log('  heap unavailable (performance.memory is Chrome-only)');
  ctx.report.cycle = [s1, s2, s3];
}

// ── unstick fixtures (run LAST — leaves synthetic colliders until reload) ────
export async function suiteUnstick(ctx: ProbeCtx): Promise<void> {
  ctx.section('unstick — embed / interior / terrain / wedge fixtures');
  const G = g();
  if (!G.__physSeat || !G.__physSelf || !G.__physAddBox) { ctx.check('probe hooks present', false); return; }
  const seat = async (x: number, z: number): Promise<boolean> => {
    if (!await ctx.teleport(x, z)) return false;
    return G.__physSeat!(x, z);
  };
  const counters = (): NonNullable<G['__unstick']> => ({ scans: 0, trapped: 0, relocations: 0, depen: 0, wedged: 0, ...G.__unstick });

  // A — structure embed → depenetration.
  const A = { x: 251, z: 75 };
  ctx.check('A: seated', await seat(A.x, A.z));
  ctx.check('A: box registered', G.__physAddBox(A.x, A.z, 0, 10));
  ctx.check('A: reads EMBEDDED', G.__physSelf()?.overlap === true);
  let freedA = false, cA = counters();
  for (let i = 0; i < 60 && !freedA; i++) { await ctx.sleep(500); const s = G.__physSelf(); if (s && !s.overlap) { freedA = true; cA = counters(); } }
  ctx.check(`A: freed with depenetration active (depen=${cA.depen})`, freedA && cA.depen > 0);

  // B — hollow interior guard.
  const B = { x: 251, z: 139 };
  ctx.check('B: seated', await seat(B.x, B.z));
  ctx.check('B: roof slab registered', G.__physAddBox(B.x, B.z, 14, 10));
  await ctx.sleep(1500);
  const sB = G.__physSelf();
  ctx.check('B: ROOFED not embedded', !!sB && !sB.overlap && sB.ceiling);
  ctx.check('B: plain scan declines', G.__unstickNow!(false) === null);
  ctx.check('B: interior escape relocates', G.__unstickNow!(true) !== null);
  await ctx.sleep(5_500);

  // C — terrain-class embed, automatic under held intent.
  const C = { x: 175, z: 139 };
  ctx.check('C: seated', await seat(C.x, C.z));
  ctx.check('C: terrain patch registered', G.__physAddTerrainPatch!(C.x, C.z, 0.9));
  ctx.check('C: reads terrain-EMBEDDED', G.__physSelf()?.terrainOverlap === true);
  const c0 = counters();
  ctx.holdIntent([0, -1]);
  let recC = false, auto = true;
  for (let i = 0; i < 30 && !recC; i++) { await ctx.sleep(500); const s = G.__physSelf(); if (s && !s.terrainOverlap && !s.overlap) recC = true; }
  if (!recC && G.__unstickNow!(false, true)) { auto = false; await ctx.sleep(500); recC = !G.__physSelf()?.terrainOverlap; }
  ctx.holdIntent(null);
  const cC = counters();
  ctx.check(`C: recovered (${auto ? 'AUTOMATIC' : 'manual any-class'}; Δdepen=${cC.depen - c0.depen})`, recC);
  await ctx.sleep(5_500);

  // D — dead-end wedge under held intent.
  const D = { x: 175, z: 75 };
  ctx.check('D: U-slot registered', G.__physAddBox(D.x - 4.2, D.z, 0, 4) && G.__physAddBox(D.x + 4.2, D.z, 0, 4) && G.__physAddBox(D.x, D.z - 4.2, 0, 4));
  ctx.check('D: seated in the slot', await seat(D.x, D.z));
  const d0 = counters();
  ctx.holdIntent([0, -1]);
  let recD = false;
  for (let i = 0; i < 50 && !recD; i++) { await ctx.sleep(500); const s = G.__physSelf(); if (s && Math.hypot(s.x - D.x, s.z - D.z) > 5) recD = true; }
  ctx.holdIntent(null);
  const cD = counters();
  ctx.check(`D: escaped (wedged=${cD.wedged}, Δreloc=${cD.relocations - d0.relocations})`, recD);
  ctx.report.counters = counters();
  ctx.log('NOTE: synthetic fixtures persist until reload.');
}

// ── versonalab ───────────────────────────────────────────────────────────────
/**
 * Group versona simulation: pick a cast in the nearest settlement, stage the
 * scripted scenarios (hunger crisis / feud / friendship / cheer / command)
 * through the admin debug reducers, sample every cast member, diff the event
 * tables into an interaction log, and report behavior verdicts. Runs the SAME
 * engine as the F4 Versona Lab window and `verify:versonalab`.
 *
 * NOTE: injection scenarios need the Commons ADMIN capability — run this in
 * YOUR normal browser (your identity holds admin), not a fresh profile. A
 * non-admin identity still runs the baseline observation and reports.
 */
export async function suiteVersonaLab(ctx: ProbeCtx): Promise<void> {
  ctx.section('versonalab — group simulation of versona behavior');
  const conn = atlas.conn;
  if (!conn) { ctx.check('connected', false); return; }

  await new Promise<void>((resolve) => {
    conn.subscriptionBuilder().onApplied(() => resolve()).subscribe(labBaseSubs());
  });
  // Nearest settlement to the player becomes the observatory.
  interface EconLite { settlementId: bigint; centerX: number; centerZ: number }
  let sid: bigint | null = null, cx = 0, cz = 0, bestD = Infinity;
  for (const e of labTbl<EconLite>(conn, 'settlementEcon')) {
    const d = Math.hypot(e.centerX - atlas.localX, e.centerZ - atlas.localZ);
    if (d < bestD) { bestD = d; sid = e.settlementId; cx = e.centerX; cz = e.centerZ; }
  }
  ctx.check('settlement economy data replicated', sid !== null, `${labTbl(conn, 'settlementEcon').length} settlements`);
  if (sid === null) return;
  await new Promise<void>((resolve) => {
    conn.subscriptionBuilder().onApplied(() => resolve()).subscribe(labScopedSubs(sid!));
  });
  ctx.log(`observatory: settlement ${sid} (${cx.toFixed(0)}, ${cz.toFixed(0)}) — teleporting in to watch`);
  await ctx.teleport(cx + 8, cz + 8);
  await ctx.sleep(4_000);

  const cast = pickCast(conn, Date.now(), sid, 6);
  ctx.check('cast assembled (≥2 adult versonas free of encounters)', !!cast, cast ? cast.ids.map((i) => `#${i}`).join(' ') : undefined);
  if (!cast) return;
  const canInject = atlas.currentWorldId !== null && capsIn(atlas.currentWorldId).admin;
  if (!canInject) ctx.log('⚠ no admin capability on this identity — observe-only (baseline); feel/behave scenarios will be skipped');

  const run = await runLab(conn, cast, {
    windowMs: ctx.fast ? 40_000 : 75_000,
    sampleMs: 3_000,
    canInject,
    log: (l) => ctx.log(l),
    sleep: (ms) => ctx.sleep(ms),
  });

  for (const s of run.scenarios) {
    if (!s.ran) continue;
    const rows: Record<string, Record<string, unknown>> = {};
    for (const c of s.checks) rows[c.label] = { ok: c.ok ? '✓' : c.hard ? '✗ HARD' : '△ soft', detail: c.detail ?? '' };
    ctx.table(`${s.name} — ${s.title}`, rows);
    for (const c of s.checks.filter((q) => q.hard)) ctx.check(`${s.name}: ${c.label}`, c.ok, c.detail);
  }
  ctx.log(`${run.interactions.length} interactions observed across the run`);
  ctx.report.markdown = renderLabReportMd(run);
  ctx.report.run = JSON.parse(labRunToJson(run));
  ctx.log('full markdown report is in the JSON node (report.suites.versonalab.markdown) — copy it out for the record');
}

// ── versonacensus ────────────────────────────────────────────────────────────
/**
 * World-wide behavioral census (READ-ONLY, no admin needed): relationship
 * network shape, emotion demographics, survival state incl. wealth Gini,
 * political participation, group structure, and information propagation
 * (per-factKey breadth). The full markdown lands in the report JSON.
 */
export async function suiteVersonaCensus(ctx: ProbeCtx): Promise<void> {
  ctx.section('versonacensus — world-wide behavioral census');
  const conn = atlas.conn;
  if (!conn) { ctx.check('connected', false); return; }
  await new Promise<void>((resolve) => {
    conn.subscriptionBuilder().onApplied(() => resolve()).subscribe([...labBaseSubs(), ...censusExtraSubs()]);
  });
  await ctx.sleep(2_000);
  const c = computeCensus(conn, Date.now());
  ctx.check('population replicated', c.population.agents > 500, `${c.population.agents} agents / ${c.population.settlements} settlements`);
  ctx.table('social network', {
    pairs: { total: c.social.pairsTotal, 'close ties/agent (≥80)': c.social.closeTiesPerAgent.mean, 'friends/agent (≥50)': c.social.friendsPerAgent.mean, 'at 100': `${(c.social.maxedShare * 100).toFixed(0)}%` },
  });
  ctx.table('emotions', {
    episodes: { n: c.emotions.episodes, feelers: c.emotions.agentsFeeling, targeted: `${(c.emotions.targetedShare * 100).toFixed(0)}%`, grudges: c.emotions.grudges.total },
  });
  ctx.table('survival', {
    state: { 'hunger mean': c.survival.hunger.mean, 'pantry p50': c.survival.pantryDays.p50, 'food-insecure': `${(c.survival.foodInsecureShare * 100).toFixed(1)}%`, 'wallet gini': c.survival.wallet.gini },
  });
  ctx.table('information', {
    memories: { n: c.information.memories.total, 'rumor share': `${(c.information.rumorShare * 100).toFixed(1)}%`, 'widest fact': c.information.propagation.top[0]?.holders ?? 0 },
  });
  ctx.check('emotion demography present', c.emotions.episodes > 50 && Object.keys(c.emotions.etypeHist).length >= 5);
  ctx.check('information propagating', c.information.memories.total > 100 && c.information.propagation.top.length > 0);
  ctx.report.markdown = renderCensusMd(c);
  ctx.report.census = JSON.parse(censusToJson(c));
  ctx.log('full census markdown is in report.suites.versonacensus.markdown');
}

export const SUITES: Record<string, (ctx: ProbeCtx) => Promise<void>> = {
  boot: suiteBoot,
  render: suiteRender,
  net: suiteNet,
  movement: suiteMovement,
  tiers: suiteTiers,
  tierstour: suiteTiersTour,
  perf: suitePerf,
  world: suiteWorld,
  memory: suiteMemory,
  unstick: suiteUnstick,
  versonalab: suiteVersonaLab,
  versonacensus: suiteVersonaCensus,
};

/**
 * 'all' order: quick reads first, teleport tours after, fixtures last.
 * `tierstour` (the full tier × site matrix, ~35–60 min) and `versonalab` (the
 * group NPC simulation, ~8 min + needs an admin identity for injections) are
 * deliberately NOT in 'all' — request them explicitly:
 * ?probe=tierstour&probeFast=1 · ?probe=versonalab.
 */
export const ALL_ORDER = ['boot', 'render', 'net', 'movement', 'tiers', 'perf', 'world', 'memory', 'unstick'];
